// # 前台可由後台維護的文案與開關
//
// 資料來源：後端 SystemConfig（group / key / value）。
//
// 前台只有一支可依 key 查詢的公開端點：
//   GET /api/Member/SystemConfig?key={key}
// 它只讀得到自己寫死的那個 group，因此後台存檔時的 group 必須與之一致
// （見 PUBLIC_CONFIG_GROUP）。既然所有頁面共用同一個 group，key 必須全域唯一，
// 所以實際送出的 key 會加上頁面前綴，例如 heroTitle → homepageHeroTitle。
//
// 這裡的 defaultValue 是「尚未設定」時的回退值，與目前線上文案一致。
//
// 新增欄位時：在這裡加 key，後台的前台內容設定頁會同步多一個欄位（兩邊 key 需一致）。

// # 公開端點寫死的 group；後台必須存進同一個 group 前台才讀得到
export const PUBLIC_CONFIG_GROUP = "Member";

// # 各頁面在共用 group 下的 key 前綴
export const KEY_PREFIX = {
  Homepage: "homepage",
  Point: "point",
  Terms: "terms",
};

// # 組出實際送往後端的 key
// 英文版在尾端加 En（homepageHeroTitle → homepageHeroTitleEn），
// 後台沒填英文時前台會自動退回中文那筆。
export const buildRemoteKey = (section, key, lang) =>
  `${KEY_PREFIX[section]}${key.charAt(0).toUpperCase()}${key.slice(1)}` +
  (lang === "en" ? "En" : "");

// # 開關類欄位不分語言
export const isLocalizedField = (field) => field.type !== FIELD_TYPE.SWITCH;

export const CONFIG_GROUP = {
  HOMEPAGE: "Homepage",
  POINT: "Point",
  TERMS: "Terms",
};

// # 欄位型別，決定後台要渲染什麼控制項
export const FIELD_TYPE = {
  SWITCH: "switch", // 開關
  TEXT: "text", // 單行文字
  TEXTAREA: "textarea", // 多行文字（換行分隔）
  IMAGE: "image", // 圖片網址
  RICH_TEXT: "richText", // 富文字
};

// # 首頁
export const homepageFields = [
  {
    key: "isMarketingBarEnabled",
    type: FIELD_TYPE.SWITCH,
    label: "顯示頂部通知條",
    defaultValue: "true",
  },
  {
    key: "marketingBarTitle",
    type: FIELD_TYPE.TEXT,
    label: "通知條大標",
    defaultValue: "✦ SKYLINE POINT 現已開放購買",
  },
  {
    key: "marketingBarSubtitle",
    type: FIELD_TYPE.TEXT,
    label: "通知條小標",
    defaultValue: "用點數輕鬆換票，加贈點數最高可多 25%",
  },
  {
    key: "marketingBarButtonText",
    type: FIELD_TYPE.TEXT,
    label: "通知條按鈕文字",
    defaultValue: "立即購買",
  },
  {
    key: "marketingBarLink",
    type: FIELD_TYPE.TEXT,
    label: "通知條按鈕連結",
    defaultValue: "/points",
  },

  {
    key: "heroImageUrl",
    type: FIELD_TYPE.IMAGE,
    label: "主視覺圖片",
    hint: "留空則使用內建預設圖。此處設定後不再輪播活動圖。",
    defaultValue: "",
  },
  {
    key: "heroLabel",
    type: FIELD_TYPE.TEXT,
    label: "主視覺小標籤",
    defaultValue: "Outdoor Cinema Experience",
  },
  {
    key: "heroTitle",
    type: FIELD_TYPE.TEXTAREA,
    label: "主視覺大標",
    hint: "一行一句，會自動換行",
    defaultValue: "在城市星空下\n看一部好電影",
  },
  {
    key: "heroSubtitle",
    type: FIELD_TYPE.TEXTAREA,
    label: "主視覺小文字",
    hint: "一行一句，會自動換行",
    defaultValue: "獨立無線耳機 × 躺椅 × 屋頂夜景\n每場放映都是一次獨特的感官旅程",
  },

  {
    key: "isPointsPromoEnabled",
    type: FIELD_TYPE.SWITCH,
    label: "顯示點數推廣區塊",
    defaultValue: "true",
  },
  {
    key: "pointsPromoLabel",
    type: FIELD_TYPE.TEXT,
    label: "點數推廣小標籤",
    defaultValue: "Skyline Point",
  },
  {
    key: "pointsPromoTitle",
    type: FIELD_TYPE.TEXT,
    label: "點數推廣大標",
    defaultValue: "用點數購票，更划算",
  },
  {
    key: "pointsPromoDescription",
    type: FIELD_TYPE.TEXT,
    label: "點數推廣說明",
    defaultValue: "購買點數方案享折扣，加贈點數最高可多 25%。",
  },
  {
    key: "pointsPromoFeatures",
    type: FIELD_TYPE.TEXTAREA,
    label: "點數推廣條列",
    hint: "一行一條",
    defaultValue: "加贈點數優惠組合\n點數無使用期限\n支援信用卡、Line Pay",
  },
  {
    key: "pointsPromoButtonText",
    type: FIELD_TYPE.TEXT,
    label: "點數推廣按鈕文字",
    defaultValue: "了解方案",
  },
];

// # 點數頁
export const pointFields = [
  {
    key: "heroLabel",
    type: FIELD_TYPE.TEXT,
    label: "點數頁小標籤",
    defaultValue: "Skyline Point",
  },
  {
    key: "heroTitle",
    type: FIELD_TYPE.TEXT,
    label: "點數頁大標",
    defaultValue: "Skyline Film Points",
  },
  {
    key: "heroSubtitle",
    type: FIELD_TYPE.TEXT,
    label: "點數頁小標",
    defaultValue: "先儲值點數，想看的夜晚再訂票",
  },
  {
    key: "heroDescription",
    type: FIELD_TYPE.TEXTAREA,
    label: "點數頁說明",
    defaultValue:
      "點數可用於 Skyline Film 屋頂電影院活動票券。購買完成後會加入會員帳戶，結帳時可直接折抵或兌換指定票種。",
  },
  {
    key: "plansTitle",
    type: FIELD_TYPE.TEXT,
    label: "方案區大標",
    defaultValue: "選擇點數票券",
  },
  {
    key: "plansDescription",
    type: FIELD_TYPE.TEXTAREA,
    label: "方案區說明",
    defaultValue:
      "每 1 點等同 NT$1。方案可依活動優惠調整，前台會同步顯示加贈與到期資訊。",
  },
  {
    key: "howItWorksTitle",
    type: FIELD_TYPE.TEXT,
    label: "使用流程標題",
    defaultValue: "使用流程",
  },
  {
    key: "howItWorksStep1Title",
    type: FIELD_TYPE.TEXT,
    label: "流程 01 標題",
    defaultValue: "購買點數",
  },
  {
    key: "howItWorksStep1Description",
    type: FIELD_TYPE.TEXTAREA,
    label: "流程 01 說明",
    defaultValue: "選擇方案並完成付款，點數會立即加入你的會員帳戶。",
  },
  {
    key: "howItWorksStep2Title",
    type: FIELD_TYPE.TEXT,
    label: "流程 02 標題",
    defaultValue: "挑選活動",
  },
  {
    key: "howItWorksStep2Description",
    type: FIELD_TYPE.TEXTAREA,
    label: "流程 02 說明",
    defaultValue: "瀏覽活動並選擇場次，在購票結帳頁面選擇使用多少點數。",
  },
  {
    key: "howItWorksStep3Title",
    type: FIELD_TYPE.TEXT,
    label: "流程 03 標題",
    defaultValue: "折抵票券",
  },
  {
    key: "howItWorksStep3Description",
    type: FIELD_TYPE.TEXTAREA,
    label: "流程 03 說明",
    defaultValue: "結帳時使用點數折抵，完成後會收到電子票券。",
  },
];

// # 服務條款 / 退換票政策
export const termsFields = [
  {
    key: "content",
    type: FIELD_TYPE.RICH_TEXT,
    label: "服務條款與退換票政策",
    hint: "留空則顯示內建的預設條款內容",
    defaultValue: "",
  },
];

export const configSchema = {
  [CONFIG_GROUP.HOMEPAGE]: homepageFields,
  [CONFIG_GROUP.POINT]: pointFields,
  [CONFIG_GROUP.TERMS]: termsFields,
};

// # 由 schema 取出某 group 的預設值 map
export const getDefaults = (group) =>
  (configSchema[group] || []).reduce((acc, field) => {
    acc[field.key] = field.defaultValue;
    return acc;
  }, {});
